import { getPatientProductsEndpointWithQueryParameters } from "utils/patient-products.util";
import { viteEnv } from "utils/vite-env.util";

import {
  formatValidQueryParamUrl,
  formatValidQueryParamUrlCommaSeparated
} from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  BulkDeliverPatientProductsInput,
  BulkDeliverPatientProductsResponse,
  DeliveriesTabCounts,
  PatientProduct,
  PatientProductFilters,
  PatientProductHistoryResponse,
  PatientProductInput,
  PatientProductStatusChangePreview,
  PatientProductUpdateInput,
  PatientProductsDownloadQuery,
  PatientProductsResponse
} from "./types/patient-products.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_PATIENT_PRODUCTS_LIMIT = 25;

export type PatientDevicesCounts = {
  all: number;
  ready_to_check_in: number;
  ready_to_be_delivered: number;
  in_repair: number;
  ordered: number;
  loaners: number;
  demos: number;
};

// Exported so the Deliveries export can be tested against the exact requests the service makes.
export const getPatientProductsListRequest = (params: PatientProductFilters) =>
  getPatientProductsEndpointWithQueryParameters("/patient-products/", params);

// The Deliveries export (SN-19665). Its own builder rather than `downloadPatientProductsCSV`'s:
// that one sends arrays as repeated keys and nested objects as `[object Object]`, which the CHS
// and Hearing Aids reports live with, and changing it would change what they send. This one takes
// already-flattened params and formats them the way the list endpoint does.
export const getDeliveriesCsvRequest = (params: Record<string, unknown>) => ({
  url: `${
    viteEnv.VITE_SUNO_API
  }/patient-products/?${formatValidQueryParamUrlCommaSeparated(params)}`,
  headers: { Accept: "custom-csv/json" }
});

export const patientProductsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientProducts: builder.query<PatientProduct[], PatientProductFilters>({
      query: getPatientProductsListRequest,
      transformResponse: (response: PatientProductsResponse) => {
        return response.results;
      },
      providesTags: (patientProducts) => {
        if (
          patientProducts?.find(
            (product) => product.assigned_tags !== undefined
          )
        ) {
          return ["PatientProducts", "PatientProductsWithAssignedTags"];
        }

        return ["PatientProducts"];
      }
    }),
    getPatientProductsUniquePatientsCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      PatientProductFilters
    >({
      query: (params) =>
        getPatientProductsEndpointWithQueryParameters(
          "/patient-products/patients-count/",
          {
            ...params,
            limit: 0,
            offset: 0
          }
        ),
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => {
        return {
          count: Number(response.count || 0),
          count_sms: Number(response.count_sms || 0),
          count_distinct_patients: Number(response.count_distinct_patients || 0)
        };
      },
      providesTags: ["PatientProducts"]
    }),
    getPatientProductsCount: builder.query<
      number,
      Omit<PatientProductFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, ...filters }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/count/?${formatValidQueryParamUrlCommaSeparated({
          ...baseFilters,
          ...filters
        })}`,
      providesTags: ["PatientProductsCounters"]
    }),
    getPatientProductsCounts: builder.query<
      PatientDevicesCounts,
      Omit<PatientProductFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, ...filters }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/counts/?${formatValidQueryParamUrlCommaSeparated({
          ...baseFilters,
          ...filters
        })}`,
      providesTags: ["PatientProducts", "PatientProductsCounters"]
    }),
    // One request for all eight Deliveries tab counts (SN-19660), in place of the seven separate
    // `/count/` calls Patient Devices fires through `Promise.all`. The counts describe exactly
    // the queryset they are handed, so the caller passes its BASE filters only - the applied
    // filter set must stay out, or the tile numbers move every time the table is narrowed.
    // `deliveries-counts`, not `counts`: that path is the Patient Devices counters (ENG-1112).
    getDeliveriesTabCounts: builder.query<
      DeliveriesTabCounts,
      Omit<PatientProductFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, ...filters }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/deliveries-counts/?${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilters,
            ...filters
          }
        )}`,
      providesTags: ["PatientProductsCounters"]
    }),
    getPatientProduct: builder.query<
      PatientProduct,
      {
        patientProductId: number;
        query?: string;
      }
    >({
      query: ({ patientProductId, query = "{*}" }) =>
        `${
          viteEnv.VITE_SUNO_API
        }/patient-products/${patientProductId}/?query=${query}`,
      providesTags: ["PatientProducts"]
    }),
    // SN-20131. Read from the audit table, so it is fetched by the device page's History card alone,
    // when that card is on screen, and never by a list. `PatientProducts` so a save or a status
    // change refetches it along with the device.
    getPatientProductHistory: builder.query<
      PatientProductHistoryResponse,
      number
    >({
      query: (patientProductId) =>
        `${viteEnv.VITE_SUNO_API}/patient-products/${patientProductId}/history/`,
      providesTags: ["PatientProducts"]
    }),
    addPatientProduct: builder.mutation<PatientProduct, PatientProductInput>({
      query: (patientProduct) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/`,
        method: "POST",
        body: patientProduct
      }),
      invalidatesTags: [
        "PatientProducts",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    updatePatientProduct: builder.mutation<
      PatientProduct,
      PatientProductUpdateInput
    >({
      query: ({ id, ...patientProduct }) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/`,
        method: "PATCH",
        body: patientProduct
      }),
      invalidatesTags: [
        "PatientProducts",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    deletePatientProduct: builder.mutation<void, number>({
      query: (id) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["PatientProducts", "MergeTagsSale", "SaleItems"]
    }),
    reconnectPatientProductInventory: builder.mutation<
      PatientProduct,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/reconnect-inventory/`,
        method: "POST"
      }),
      invalidatesTags: [
        "PatientProducts",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    // A mutation only because it is a POST: the dry run writes nothing, so it invalidates nothing.
    // Same modelling as the catalog CSV preview (`previewProductsCatalogCSV`).
    previewBulkDeliverPatientProducts: builder.mutation<
      BulkDeliverPatientProductsResponse,
      number[]
    >({
      query: (patientProductIds) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/bulk-deliver/`,
        method: "POST",
        body: { patient_product_ids: patientProductIds, dry_run: true }
      })
    }),
    // Beyond the device rows and the tab counts, delivering sells stock out and closes orders,
    // and it moves the same merge tags and sale items a single-device delivery PATCH does.
    bulkDeliverPatientProducts: builder.mutation<
      BulkDeliverPatientProductsResponse,
      BulkDeliverPatientProductsInput
    >({
      query: (body) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/bulk-deliver/`,
        method: "POST",
        body: { ...body, dry_run: false }
      }),
      invalidatesTags: [
        "PatientProducts",
        "PatientProductsCounters",
        "InventoryProducts",
        "InventoryProductCounters",
        "Orders",
        "MergeTagsPatientProduct",
        "MergeTagsSale",
        "SaleItems"
      ]
    }),
    // ENG-1037/1041: read-only diff of a proposed terminal status change, consumed by the
    // preview/edit modal. No tag invalidation.
    previewPatientProductStatusChange: builder.query<
      PatientProductStatusChangePreview,
      { id: number; status: number }
    >({
      query: ({ id, status }) => ({
        url: `${viteEnv.VITE_SUNO_API}/patient-products/${id}/preview-status-change/?status=${status}`
      })
    }),
    downloadPatientProductsCSV: builder.query<
      CSVReportResponse,
      PatientProductsDownloadQuery
    >({
      query: ({ query = "{*}", csvHeader = "*", csvLabels, ...filters }) => ({
        url: `${
          viteEnv.VITE_SUNO_API
        }/patient-products/?query=${query}&csv_header=${csvHeader}&${formatValidQueryParamUrl(
          { csv_labels: csvLabels, ...filters }
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    }),
    downloadDeliveriesCSV: builder.query<
      CSVReportResponse,
      Record<string, unknown>
    >({
      query: getDeliveriesCsvRequest
    })
  })
});

export const {
  useGetPatientProductsQuery,
  useGetPatientProductsUniquePatientsCountQuery,
  useAddPatientProductMutation,
  useUpdatePatientProductMutation,
  useGetPatientProductQuery,
  useGetPatientProductsCountQuery,
  useLazyGetPatientProductsCountQuery,
  useGetPatientProductsCountsQuery,
  useGetDeliveriesTabCountsQuery,
  useLazyDownloadPatientProductsCSVQuery,
  useLazyDownloadDeliveriesCSVQuery,
  useDeletePatientProductMutation,
  useLazyGetPatientProductsQuery,
  useReconnectPatientProductInventoryMutation,
  usePreviewPatientProductStatusChangeQuery,
  usePreviewBulkDeliverPatientProductsMutation,
  useBulkDeliverPatientProductsMutation,
  useGetPatientProductHistoryQuery
} = patientProductsApi;
